import React, { useEffect, useMemo, useState } from "react"
import { Button, Card, Divider } from "semantic-ui-react"
import { connect } from "react-redux"
import { withTranslation } from "react-i18next"
import { withRouter } from "react-router-dom"
import { routerPaths } from "../../../../v1/constants/paths"
import ServiceCard from "./ServiceCard"
import { updateCart } from "../../../../v1/actions/user"
import { toggleService } from "./SelectService"
import { getCustomServiceCategories } from "../../../../v1/actions/services"
import { CA } from "../../../../v1/constants/states"
import { getSubdomain } from "../../../../v1/components/Config/StylesheetInjector"

const ReviewServices = ({
  t,
  history,
  cart,
  order,
  vehicle,
  updateCart,
  location,
  user,
  getCustomServiceCategories,
  membershipData
}) => {
  const services = cart.order_services || []
  const [selectedPositions, setSelectedPositions] = useState({})
  const [serviceErrors, setServiceErrors] = useState({})
  const [selectedServices, setSelectedServices] = useState([])
  const [servicesUpdating, setServicesUpdating] = useState([])
  const [categoryServices, setCategoryServices] = useState([])
  const [modifiedSelectedServices, setModifiedSelectedServices] = useState({})

  const maintenanceService = new URLSearchParams(location.search).get("maintenance_service")
  const diagnosisService = new URLSearchParams(location.search).get("diagnosis_service")

  useEffect(() => {
    setSelectedServices(cart.order_services.map((service) => service.id))
  }, [cart.order_services])

  const membership = useMemo(() => {
    if (!membershipData?.length) return null
    return [...membershipData].find((plan) => plan.name === "Premium")
  }, [membershipData])

  const handleSubmit = async () => {
    if (user.country === CA) {
      history.push(routerPaths.selectDate)
      return
    }
    history.push({
      pathname: routerPaths.shopSelection
    })
  }

  const updateService = async (service, position = null) => {
    toggleService({
      service,
      positionOverride: position,
      cart,
      order,
      vehicle,
      selectedPositions,
      setSelectedPositions,
      setServiceErrors,
      setServicesUpdating,
      setSelectedServices,
      t,
      updateCart
    })
  }

  const getPositions = (positions) => {
    return positions.map((position, index) => ({
      key: index,
      text: position,
      value: position
    }))
  }

  const handleAddMoreService = () => {
    history.push({
      pathname: routerPaths.selectService
    })
  }

  const validUserData = !!user?.cellPhone && (user?.lastName?.length || 0) > 0
  const isComparePriceFlow = localStorage.getItem("isComparePriceFlow") === "true"

  if (!validUserData && !isComparePriceFlow) {
    history.replace({ pathname: routerPaths.updateUser, search: location.search })
    return null
  }

  const fetchAvailableCategories = async () => {
    let categories = (await getCustomServiceCategories()) || []

    let categoryServices = categories?.flatMap((item) => [
      ...item.default,
      ...item.related_services
    ])

    setCategoryServices(categoryServices)
  }

  useEffect(() => {
    fetchAvailableCategories()
  }, [])

  useEffect(() => {
    const services = categoryServices
      .filter((service) => selectedServices.includes(service.id))
      .reduce((acc, service) => {
        acc[service.id] = service
        return acc
      }, {})
    setModifiedSelectedServices(services)
  }, [categoryServices, selectedServices])

  return (
    <>
      {services.length === 0 ? (
        <Card className="review-services-card" raised={true}>
          <div className="service-card">
            <p className="empty-services-label">Your service list is empty!</p>
            <div className="action_button flex-col">
              <Button fluid onClick={handleAddMoreService} className="primary-button">
                View service categories
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        <Card className="review-services-card" raised={true}>
          <div className="review-services-info">
            {!maintenanceService && !diagnosisService && (
              <p className="you-have-selected">Your services</p>
            )}
            {maintenanceService && (
              <p className="you-have-selected">
                Recommended services for your vehicle at{" "}
                <b>{new Intl.NumberFormat("en-US").format(maintenanceService)}</b> miles
              </p>
            )}
            {diagnosisService && (
              <p className="you-have-selected">
                A shop will need to perform an inspection to confirm the exact issue.
              </p>
            )}
            <div className="review-services-list">
              {services.sort().map((service) => {
                const modifiedService = { ...service, ...modifiedSelectedServices[service.id] }
                return (
                  <React.Fragment key={service.id}>
                    <ServiceCard
                      service={modifiedService}
                      selectedServices={selectedServices}
                      selectedPositions={selectedPositions}
                      serviceErrors={serviceErrors}
                      handleService={updateService}
                      updateServiceWithPosition={updateService}
                      getPositions={getPositions}
                      isUpdating={servicesUpdating.includes(service.id)}
                    />
                  </React.Fragment>
                )
              })}
            </div>
            <p onClick={handleAddMoreService} className="add-edit-services-link">
              {t("addOrEditServices")}
            </p>
            <Divider className="p" />
            <p className="final-price-text">
              {getSubdomain() === "instacart-ca"
                ? t("finalPriceTextInstacartCA")
                : t("finalPriceText")}
            </p>
          </div>
          <div className="service-card">
            <div className="action_button flex-col">
              <Button
                fluid
                onClick={handleSubmit}
                className="primary-button"
                disabled={services.length == 0}
              >
                {isComparePriceFlow
                  ? t("welcomeModal:comparePrices")
                  : user.country === CA
                  ? t("Continue")
                  : t("ContinueToSelectShop")}
              </Button>
            </div>
          </div>
        </Card>
      )}
    </>
  )
}

function mapStateToProps(state, props) {
  let user = state.user || {}
  let activeVehicle = (user.vehicles || []).find((v) => v.id === user.activeVehicleId) || {}
  let cart
  let currentActiveOrder =
    user.active_orders && user.active_orders.find((o) => o.vehicle_id == user.activeVehicleId)

  if (currentActiveOrder) {
    cart = currentActiveOrder
  } else {
    if (props.cartId) {
      cart = user.active_carts && user.active_carts.filter((c) => c.id == Number(props.cartId))[0]
    } else {
      cart =
        user.active_carts && user.active_carts.find((c) => c.vehicle_id == user.activeVehicleId)
    }
  }

  if (!cart || !cart.order_services) {
    cart = { estimated_total: 0, order_services: [] }
  }

  return {
    user: user,
    cart: cart,
    order: currentActiveOrder,
    vehicle: activeVehicle,
    membershipData: state.membershipData.availableMembershipPlans
  }
}

export default withRouter(
  connect(mapStateToProps, {
    updateCart,
    getCustomServiceCategories
  })(withTranslation("addService")(ReviewServices))
)
